/**
 * Get the iframe of the editor. Use in FSE or Mobile/Tablet Preview for Page/Post.
 */
const getEditorIframe = () => ( document.querySelector( 'iframe[name^="editor-canvas"]' ) );

/**
 * Class that request the fonts and loaded them into the browser.
 */
class GoogleFontsLoader {

	/**
	 * Initialize.
	 */
	constructor() {
		this.fonts = [];
		this.status = 'none';

		this.controller = new AbortController();
		this.request = null;

		this.node = document.createElement( 'style' );
		this.node.type = 'text/css';
		this.node.setAttribute( 'data-generator', 'vayu-blocks-fonts-loader' );
		this.isAttaching = false;

		this.usedFonts = [];
	}

	/**
	 * The the loader after is loaded.
	 *
	 * @returns {Promise<GoogleFontsLoader>}
	 */
	async afterLoading() {
		await this.requestFonts();
		return this;
	}

	/**
	 * Get the font.
	 *
	 * @param {string} fontName The name of the font.
	 * @returns {import('../components/google-fonts-control/types').GoogleFontItem}
	 */
	getFont( fontName ) {
		return this.fonts.find( font => font.family === fontName );
	}

	/**
	 * Get the variants of the font.
	 *
	 * @param {string} fontName The name of the font.
	 * @returns {{label: string, value: string}[]}
	 */
	getVariants( fontName ) {

		const toLower = (str) => {
			if (typeof str !== 'string') {
			  throw new Error('Input must be a string');
			}
		  
			return str.toLowerCase();
		  };
		
		const startCase = (str) => {
			if (typeof str !== 'string') {
			  throw new Error('Input must be a string');
			}
		  
			return str
			  .replace(/_/g, ' ') // Replace underscores with spaces
			  .replace(/(?:^|\s)\S/g, (match) => match.toUpperCase()); // Capitalize first letter of each word
		  };
		  
		const font = this.getFont( fontName );
		if ( font ) {
			return ( font.variants )
				.filter( o => false === o.includes( 'italic' ) )
				.map( o => {
					return o = {
						'label': startCase( toLower( o ) ),
						'value': o
					};
				});
		}
		return [];
	}

	/**
	 * Load the font to brower document. Can inject to an iframe.
	 *
	 * @param {string} fontName The name of the font.
	 * @param {string} variant The font variant.
	 * @returns {Promise<Error|import('../components/google-fonts-control/types').GoogleFontItem}
	 */
	async loadFontToBrowser( fontName, variant = 'regular' ) {
		if ( ! fontName ) {
			return Error( 'Empty font name.' );
		}

		if ( 'none' === this.status || 'loading' === this.status ) {
			await this.afterLoading();
		}

		const font = this.getFont( fontName );

		if ( ! font ) {
			return Error( 'Font does not exists.' );
		}

		if ( ! this.usedFonts.find( ( _ ) => _.font.family === fontName && _.variant === variant ) ) {
			this.usedFonts.push({ font, variant });
			this.updateCSSNode();
		}

		return font;
	}

	/**
	 * Make a request to get the font list.
	 *
	 * @param {boolen} force Force the request to trigger again.
	 * @returns {Promise<import('../components/google-fonts-control/types').GoogleFontItem[] | Promise<import('../components/google-fonts-control/types').GoogleFontItem[]>>} Return the result or the request that is in pending.
	 */
	async requestFonts(force = false) {
		if (this.status === 'done') {
			return this.fonts;
		}

		if (this.status === 'none') {
			this.status = 'loading';
			this.request = new Promise(async (resolve) => {
				if (this.status === 'done' && !force) {
					resolve(this.fonts);
				}

				if (force) {
					try {
						this.controller.abort();
					} catch (_) {}
					this.controller = new AbortController();
				}

				try {
					const apiRoot = window?.wpApiSettings?.root || '/wp-json/';
					const response = await fetch(`${apiRoot}vayu-blocks/v1/google-fonts`, {
						signal: this.controller.signal,
						credentials: 'same-origin',
						headers: {
							'X-WP-Nonce': window?.wpApiSettings?.nonce || '',
						}
					});

					if (!response.ok) throw new Error('Font fetch failed');

					const data = await response.json();
					this.fonts = data.items || [];
					this.status = 'done';
					resolve(this.fonts);
				} catch (_) {
					this.status = 'error';
					resolve([]); //  resolve with empty array to suppress all console errors
				}
			});
		}

		return this.request;
	}

	
	/**
	 * Update the node CSS.
	 */
	updateCSSNode() {
		this.node.innerHTML = this.renderCSSFont();
		this.attach();
	}

	attach() {
		const currentDocument = getEditorIframe()?.contentWindow?.document ?? document;
	
		if ( currentDocument ) {
			// Clone a new node every time with unique ID or data-key
			const clonedNode = this.node.cloneNode( true );
			const uniqueId = `vayu-blocks-fonts-loader-${Math.random().toString(36).substr(2, 9)}`;
			clonedNode.setAttribute( 'data-generator', uniqueId );
	
			currentDocument.head?.appendChild( clonedNode );
		}
	}

	/**
	 * Render the Font Face for every used font.
	 */
	renderCSSFont() {
		return Array.from( this.usedFonts ).map( ({ font, variant }) => {
			const url = ( font.files?.[variant] ?? font.files?.regular )?.replace( 'http://', 'https://' );
			return `
				@font-face {
					font-family: "${font.family}";
					src: url('${url}'); /* IE9 Compat Modes */
					src: url('${url}')  format('truetype'), /* Safari, Android, iOS */
				}
			`;
		}).join( '\n' );
	}
}

const googleFontsLoader = new GoogleFontsLoader();

Object.seal( googleFontsLoader );

export default googleFontsLoader;